import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../hooks/useRedux';
import { fetchLoyaltyPointsHistory } from '../features/loyalty/loyalty.slice';
import '../scss/loyalty-points-activity.component.scss';

interface PointsActivityItem {
    id: string;
    icon?: string;
    title?: string;
    description?: string;
    points?: number;
    date?: string;
    isMilestone?: boolean;
    comment?: string;
    data?: string;
    pointsGranted?: number;
    pointsGrantedDate?: string;
    actionTypeId?: number;
}

interface LoyaltyPointsActivityProps {
    onClose: () => void;
}

const LoyaltyPointsActivity: React.FC<LoyaltyPointsActivityProps> = ({ onClose }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const isSwedish = i18n.language === 'sv-se' || i18n.language === 'sv';
    const pointsHistoryData = useAppSelector((state) => state.loyalty?.loyaltyPointsHistoryData);
    const pointsHistoryLoading = useAppSelector((state) => state.loyalty?.pointsHistoryLoading || false);
    const pointsHistoryError = useAppSelector((state) => state.loyalty?.pointsHistoryError);

    const formatDate = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };
    const formatPoints = (points: number): string => {
        if (isSwedish) {
            return points.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        }
        return points.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };
    useEffect(() => {
        dispatch(fetchLoyaltyPointsHistory({ pageNumber: 1, pageSize: 50 }));
    }, [dispatch]);
    const getActivityItems = (): PointsActivityItem[] => {
        if (pointsHistoryData?.response && pointsHistoryData.response.length > 0) {
            return pointsHistoryData.response.map((item, index) => ({
                ...item,
                id: item.id || `activity-${index}`,
                date: item.date || item.pointsGrantedDate || new Date().toISOString(),
                points: item.points || item.pointsGranted || 0,
                title: item.title || item.comment || 'Activity',
                description: item.description || item.data || '',
                actionTypeId: item.actionTypeId || 1,
            }));
        }
        return [];
    };

    const activityItems = getActivityItems();

    const getIconComponent = (iconType: number) => {
        switch (iconType) {
            case 1:
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M4.16669 6.66667C4.16669 4.36548 4.16669 3.21489 4.85702 2.52456C5.54736 1.83333 6.69794 1.83333 9.00002 1.83333H11C13.3021 1.83333 14.4527 1.83333 15.143 2.52456C15.8334 3.21489 15.8334 4.36548 15.8334 6.66667V16.6667C15.8334 17.4833 15.8334 17.8917 15.5834 18.0417C15.3334 18.1917 14.9917 17.9917 14.3084 17.5917L12.1417 16.2583C11.4084 15.825 11.0417 15.6083 10.6667 15.6083H9.33335C8.95835 15.6083 8.59169 15.825 7.85835 16.2583L5.69169 17.5917C5.00835 17.9917 4.66669 18.1917 4.41669 18.0417C4.16669 17.8917 4.16669 17.4833 4.16669 16.6667V6.66667Z" stroke="#6750B8" strokeWidth="1.5" />
                    </svg>
                );
            case 2:
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M4.16669 6.66667C4.16669 4.36548 4.16669 3.21489 4.85702 2.52456C5.54736 1.83333 6.69794 1.83333 9.00002 1.83333H11C13.3021 1.83333 14.4527 1.83333 15.143 2.52456C15.8334 3.21489 15.8334 4.36548 15.8334 6.66667V16.6667C15.8334 17.4833 15.8334 17.8917 15.5834 18.0417C15.3334 18.1917 14.9917 17.9917 14.3084 17.5917L12.1417 16.2583C11.4084 15.825 11.0417 15.6083 10.6667 15.6083H9.33335C8.95835 15.6083 8.59169 15.825 7.85835 16.2583L5.69169 17.5917C5.00835 17.9917 4.66669 18.1917 4.41669 18.0417C4.16669 17.8917 4.16669 17.4833 4.16669 16.6667V6.66667Z" stroke="#6750B8" strokeWidth="1.5" />
                        <path d="M7.5 9.16667L9.16667 10.8333L12.5 7.5" stroke="#6750B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                );
            case 3:
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="6.66667" r="3.33333" stroke="#6750B8" strokeWidth="1.5" />
                        <ellipse cx="10" cy="14.1667" rx="6.66667" ry="3.33333" stroke="#6750B8" strokeWidth="1.5" />
                    </svg>
                );
            default:
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="8" stroke="#6750B8" strokeWidth="1.5" />
                    </svg>
                );
        }
    };

    const getTitleByActionType = (actionTypeId: number): string => {
        switch (actionTypeId) {
            case 1:
                return t('LOYALTY_POINTS_ACTIVITY_FIRST_INVOICE');
            case 2:
                return t('LOYALTY_POINTS_ACTIVITY_MONTHLY_MILESTONE');
            case 3:
                return t('LOYALTY_POINTS_ACTIVITY_YEARLY_REVIEW');
            case 4:
                return t('LOYALTY_POINTS_ACTIVITY_BIRTHDAY');
            case 5:
                return t('LOYALTY_POINTS_ACTIVITY_REACHED_MILESTONE');
            case 6:
                return t('LOYALTY_POINTS_ACTIVITY_DOWNGRADE_MILESTONE');
            case 7:
                return t('LOYALITY_POINTS_GIFT_FROM_ADMIN');
            default:
                return t('LOYALTY_POINTS_ACTIVITY_DEFAULT_TITLE');
        }
    };

    const getDescriptionByActionType = (actionTypeId: number): string => {
        switch (actionTypeId) {
            case 1:
                return t('LOYALTY_POINTS_ACTIVITY_FIRST_INVOICE_DESC');
            case 2:
                return t('LOYALTY_POINTS_ACTIVITY_MONTHLY_MILESTONE_DESC');
            case 3:
                return t('LOYALTY_POINTS_ACTIVITY_YEARLY_REVIEW_DESC');
            case 4:
                return t('LOYALTY_POINTS_ACTIVITY_REVIEW_LEFT_DESC');
            case 5:
                return t('LOYALTY_POINTS_ACTIVITY_REACHED_MILESTONE_DESC');
            case 6:
                return t('LOYALTY_POINTS_ACTIVITY_ACCOUNT_VERIFIED_DESC');
            case 7:
                return t('LOYALTY_POINTS_ACTIVITY_PENSION_SETUP_DESC');
            case 8:
                return t('LOYALTY_POINTS_ACTIVITY_HOLIDAY_SETUP_DESC');
            default:
                return t('LOYALTY_POINTS_ACTIVITY_DEFAULT_DESC') || 'No description available';
        }
    };
    return (
        <>
            <div className="points-activity-backdrop" onClick={onClose} />
            <div className="points-activity-panel">
                <div className="points-activity-header">
                    <div className="header-content-activity">
                        <h2 className="header-title">{t('LOYALTY_POINTS_ACTIVITY_TITLE')}</h2>
                        <button
                            className="close-button"
                            onClick={onClose}
                            aria-label={t('LOYALTY_POINTS_ACTIVITY_CLOSE')}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                    <p className="header-description">
                        {t('LOYALTY_POINTS_ACTIVITY_DESCRIPTION')}
                    </p>
                </div>

                <div className="points-activity-content">
                    {pointsHistoryLoading && (
                        <div className="loading-container" style={{ textAlign: 'center', padding: '2rem' }}>
                            <div className="spinner" style={{
                                display: 'inline-block',
                                width: '32px',
                                height: '32px',
                                border: '3px solid #f3f3f3',
                                borderTop: '3px solid #6750B8',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                            <p style={{ marginTop: '1rem', color: '#666' }}>
                                {t('LOYALTY_POINTS_ACTIVITY_LOADING') || 'Loading points history...'}
                            </p>
                        </div>
                    )}

                    {pointsHistoryError && (
                        <div className="error-container" style={{ textAlign: 'center', padding: '2rem', color: '#d32f2f' }}>
                            <p>{t('LOYALTY_POINTS_ACTIVITY_ERROR') || 'Failed to load points history. Please try again.'}</p>
                        </div>
                    )}

                    {!pointsHistoryLoading && !pointsHistoryError && (
                        <div className="activity-list">
                            {activityItems && activityItems.length > 0 ? (
                                activityItems.map((item) => (
                                    <div key={item.id} className="activity-item">
                                        <div className="activity-main">
                                            <div className="activity-details">
                                                <div className="activity-header">
                                                    <div className="activity-icon">
                                                        {getIconComponent(item.actionTypeId || 1)}
                                                    </div>
                                                    <h3 className="activity-title">{getTitleByActionType(item.actionTypeId || 1)}</h3>
                                                </div>
                                                <p className="activity-description">{getDescriptionByActionType(item.actionTypeId || 1)}</p>
                                            </div>

                                            <div className="activity-points-section">
                                                {!item.isMilestone && (
                                                    <div className="points-earned">
                                                        +{formatPoints(item.points || item.pointsGranted || 0)} {t('LOYALTY_POINTS_ACTIVITY_POINTS')}
                                                    </div>
                                                )}
                                                <div className="activity-divider"></div>
                                                <div className="activity-date">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                                        <path d="M3.33333 1.66667V2.5M6.66667 1.66667V2.5M1.66667 3.33333H8.33333M2.5 1.66667H7.5C7.96667 1.66667 8.33333 2.03333 8.33333 2.5V7.5C8.33333 7.96667 7.96667 8.33333 7.5 8.33333H2.5C2.03333 8.33333 1.66667 7.96667 1.66667 7.5V2.5C1.66667 2.03333 2.03333 1.66667 2.5 1.66667Z" stroke="#414651" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    <span>{formatDate(item.date || item.pointsGrantedDate || new Date().toISOString())}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-activity-container" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                                    <p>{t('LOYALTY_POINTS_ACTIVITY_NO_DATA') || 'No activity data available.'}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default LoyaltyPointsActivity;
